import {JW_TYPE, RoadKillSymbolStates, SimpleReelModelState, SYMBOL_NAME} from "../component/enums/RoadKillEnums";
import {RoadKillSymbol} from "../reels/RoadKillSymbol";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {RoadKill} from "../RoadKill";
import {Linear, TimelineLite} from "gsap";
import {IWinRatioConfigHighLight, RoadKillWinConfigs} from "../RoadKillWinConfigs";
import {RoadKillBackgroundElements} from "../view/RoadKillBackgroundElements";
import {RoadKillReelsView} from "../reels/RoadKillReelsView";
import {LifeView} from "../view/LifeView";
import {RoadKillWinToggle} from "../view/RoadKillWinToggle";
import {JumpingWildView} from "../view/JumpingWildView";
import {RoadKillWinView} from "../view/RoadKillWinView";
import {RoadKillWinCapView} from "../view/RoadKillWinCapView";
import {RoadKillBigWinView} from "../view/RoadKillBigWinView";
import {TopAnimalCounterView} from "../view/TopAnimalCounterView";
import {JumpingWild} from "../component/JumpingWild";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {CustomWiggle} from "gsap/dist/CustomWiggle";

export class RoadKillUtils {

    public static createSymbol(symbolName: string, state: RoadKillSymbolStates, reelId: number): RoadKillSymbol {
        return new RoadKillSymbol(symbolName as SYMBOL_NAME, reelId).init(state);
    }

    public static eachForEach(arr: any[][], func: (firstIndx: number, secondIndx: number, value: any) => void): void {
        for (let i: number = 0; i < arr.length; i++) {
            for (let k: number = 0; k < arr[i].length; k++) {
                func(i, k, arr[i][k])
            }
        }
    };

    public static fetchLastValue<T>(array: T[]): T {
        return array[array.length - 1];
    }

    public static initArrayWithValues<T>(length: number, creator: (index: number) => T): T[] {
        const array: T[] = [];
        for (let i: number = 0; i < length; i++) {
            array.push(creator(i));
        }
        return array;
    }

    public static setTimeline(tl?: gsap.core.Timeline | undefined, tlReplace?: gsap.core.Timeline | undefined, playToEnd: boolean = false): gsap.core.Timeline {
        if (!tl) return tlReplace ? tlReplace : new gsap.core.Timeline();

        if (playToEnd) tl.totalProgress(1);

        tl.pause();
        tl.kill();
        tl.clear(true);
        tl = undefined;

        return tlReplace ? tlReplace : new gsap.core.Timeline();
    }

    public static getSymbol(reelId: number, symId: number): RoadKillSymbol {
        return RoadKill.reelView.getVisibleSymbolAt(reelId, symId);
    }

    public static getSymAbsolutePos(reelId: number, rowId: number): PIXI.Point {
        const startX: number = 72;
        const startY: number = 216;
        const posX: number = startX + (reelId * RoadKillGameConfig.SYMBOL_WIDTH);
        const posY: number = startY + (rowId * RoadKillGameConfig.SYMBOL_HEIGHT);
        return new PIXI.Point(posX, posY);
    }

    public static getJWType(symName: string): JW_TYPE {
        let type: any;
        if (symName === "SW1" || symName === "Bear") {
            type = JW_TYPE.BEAR;
        } else if (symName === "SW2" || symName === "Deer") {
            type = JW_TYPE.DEER;
        } else if (symName === "SW3" || symName === "Rabbit") {
            type = JW_TYPE.RABBIT;
        } else if (symName === "Trap") {
            type = JW_TYPE.TRAP;
        }
        return type;
    }

    public static getCountUpBounceIntensity(): number {
        const winType: string = RoadKillWinConfigs.getWinType(RoadKill.model.winRatio);
        switch (winType) {
            case RoadKillWinConfigs.TINY:
                return 0;
            case RoadKillWinConfigs.SMALL:
                return 1;
            case RoadKillWinConfigs.MEDIUM:
                return 2;
            case RoadKillWinConfigs.LARGE:
                return 4;
        }
        return 0;
    }

    public static allReelsInIdleState(): boolean {
        for (let reelId: number = 0; reelId < RoadKillGameConfig.REELS_NUM; reelId++) {
            if (RoadKill.reelView.reelModels[reelId].state !== SimpleReelModelState.IDLE) {
                return false;
            }
        }
        return true;
    }

    public static hideSW(reelId: number, symId: number, duration: number = 0): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(RoadKill.model.data.evaluatedArea[reelId][symId]) > -1) {
            const jw: JumpingWild | undefined = RoadKill.jumpingWildView.getJWByAliasName(RoadKill.model.data.evaluatedArea[reelId][symId] as SYMBOL_NAME);
            if (jw?.visible && jw.reelPosition && jw.alpha) {
                tl.to(jw, {duration: 0.001, alpha: 0}, 0);
            }
        }
        return tl;
    }

    public static fetchArrTypeValue<T>(typeArr: T | T[], indx: number): T {
        return (Array.isArray(typeArr) ? (typeArr as T[])[indx] : typeArr as T);
    }


    public static winIdx(): IWinRatioConfigHighLight {
        const winRatio: number = RoadKill.model.winRatio,
            allWinRatios: IWinRatioConfigHighLight[] = RoadKillWinConfigs.WIN_RATIOS.normalWin.concat().concat(RoadKillWinConfigs.WIN_RATIOS.bigWin);
        for (let i: number = allWinRatios.length - 1; i >= 0; i--) {
            const winLevel = allWinRatios[i];
            if (winRatio >= winLevel.ratio) {
                return winLevel;
            }
        }
        throw "Could'nt find WinRatio config!";
    }

    public static fsTransition(durr: number, fromLeft: boolean, movingObj: RoadKillBackgroundElements | RoadKillReelsView | LifeView | RoadKillWinToggle | JumpingWildView | RoadKillWinView | RoadKillBigWinView | RoadKillWinCapView | TopAnimalCounterView, setXPosForcefully: boolean = false): void {
        let startToX: number = fromLeft ? -1680 : 1680;
        const movingTl: TimelineLite = new TimelineLite(),
            startPos: number = 0;
        movingTl.to(movingObj, {x: startToX, duration: durr / 2}, startPos);
        movingTl.to(movingObj, {alpha: 0, duration: durr / 4}, startPos);
        movingTl.add(() => {
            startToX < 0 ? movingObj.x = 1680 : movingObj.x = -1680;

        });
        movingTl.to(movingObj, {x: 0, duration: durr / 2.8}, startPos + durr / 2);
        movingTl.to(movingObj, {alpha: 1, duration: durr / 2}, startPos + durr / 2 + 0.07);
        if (setXPosForcefully) {
            movingTl.add(() => {
                movingObj.position.x = -3;
            })
        }
    }

    public static adjustFontSize(textObj: PIXI.Text, width: number): void {
        while (textObj.width > width) {
            textObj.style.fontSize--;
            textObj.updateTransform();
        }
    }

    public static adjustBitmapFontSize(textObj: PIXI.BitmapText, width: number): void {
        while (textObj.width > width) {
            textObj.fontSize--;
            //textObj.updateTransform();
        }
    }

    public static shake(dur: number, wiggles: number = 40): TimelineLite {
        let moveXVal: number = 5,
            moveYVal: number = 3;
        // if (dur >= 1) {
        //     moveXVal = 2
        //     moveYVal = 0.8
        // }
        let wiggleTl: TimelineLite = new TimelineLite(),
            x: string = Math.random() > 0.5 ? `-=${moveXVal}` : `+=${moveXVal}`,
            y: string = Math.random() > 0.5 ? `-=${moveYVal}` : `-=${moveYVal}`;
        const shakeWiggle = CustomWiggle.create("shakeWiggle", {
            wiggles: wiggles,
            amplitudeEase: Linear.easeNone,
            timingEase: Linear.easeNone
        });
        wiggleTl.to(NolimitApplication.pixiApp.stage, {
            duration: dur,
            x: x,
            ease: shakeWiggle
        }, 0);
        wiggleTl.to(NolimitApplication.pixiApp.stage, {
            duration: dur,
            y: y,
            ease: shakeWiggle
        }, 0);
        return wiggleTl;
    }
}